import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { useLanguage } from '@/context/LanguageContext';
import { Instagram, Facebook, ExternalLink, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Social = () => {
  const { t } = useLanguage();
  const [socialLinks, setSocialLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const response = await axios.get(`${API}/social-links`);
        setSocialLinks(response.data);
      } catch (error) {
        console.error('Error fetching social links:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLinks();
  }, []);

  const getIcon = (platform) => {
    const name = platform.toLowerCase();
    if (name.includes('instagram')) return Instagram;
    if (name.includes('facebook')) return Facebook;
    return ExternalLink;
  };

  const getColor = (platform) => {
    const name = platform.toLowerCase();
    if (name.includes('instagram')) return 'from-purple-500 to-pink-500';
    if (name.includes('facebook')) return 'from-blue-600 to-blue-500';
    return 'from-slate-600 to-slate-500';
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-slate-100 to-slate-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="text-xs font-bold tracking-widest uppercase text-blue-600 mb-4 block animate-fadeInUp">
            {t('Réseaux sociaux', 'Soziale Medien')}
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6 animate-fadeInUp delay-100 font-outfit">
            {t('Me suivre', 'Folge mir')}
          </h1>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed animate-fadeInUp delay-200 max-w-2xl mx-auto">
            {t(
              "L'aventure continue aussi en dehors du blog. Sur les réseaux sociaux, je partage le quotidien en Allemagne, les coulisses de l'expérience et des conseils rapides.",
              "Das Abenteuer geht auch außerhalb des Blogs weiter. In den sozialen Medien teile ich den Alltag in Deutschland, Einblicke hinter die Kulissen und schnelle Tipps."
            )}
          </p>
        </div>
      </section>

      {/* Social Links */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : socialLinks.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {socialLinks.map((link, index) => {
                const Icon = getIcon(link.platform);
                const gradient = getColor(link.platform);
                
                return (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group"
                    data-testid={`social-link-${index}`}
                  >
                    <div className={`bg-gradient-to-r ${gradient} rounded-3xl p-8 text-white card-hover`}>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                          <Icon className="w-8 h-8" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold font-outfit">{link.platform}</h3>
                          <p className="text-white/80 text-sm">{t('Suivre', 'Folgen')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-white/90 group-hover:text-white transition-colors">
                        <ExternalLink className="w-4 h-4" />
                        <span className="text-sm">{t('Ouvrir le profil', 'Profil öffnen')}</span>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-slate-600">
                {t('Liens bientôt disponibles !', 'Links bald verfügbar!')}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* What I Share */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-outfit">
              {t('Ce que je partage', 'Was ich teile')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { text: t('Le quotidien en Allemagne', 'Der Alltag in Deutschland'), icon: '🇩🇪' },
              { text: t("Les coulisses de l'expérience", 'Die Hinter-den-Kulissen-Erfahrung'), icon: '🎬' },
              { text: t('Des conseils rapides', 'Schnelle Tipps'), icon: '💡' },
              { text: t('Des moments vrais, sans filtre', 'Echte Momente, ungefiltert'), icon: '✨' }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200 rounded-2xl p-6 text-center card-hover"
                data-testid={`share-item-${index}`}
              >
                <span className="text-4xl mb-4 block">{item.icon}</span>
                <p className="text-slate-700 font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-blue-600">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Heart className="w-16 h-16 mx-auto mb-8 text-white animate-float" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-outfit">
            {t('Rejoins la communauté !', 'Tritt der Community bei!')}
          </h2>
          <p className="text-xl text-blue-100">
            {t(
              "Ensemble, partageons nos expériences et inspirons d'autres jeunes à oser partir.",
              "Gemeinsam teilen wir unsere Erfahrungen und inspirieren andere junge Menschen, aufzubrechen."
            )}
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default Social;
